import { useIsArchonApp } from '@resources/js/contexts/useIsArchonApp';
import { signIn, useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

type ArchonAppIntegrationContext = {
  isArchonApp: boolean;
  iframeId: string | null;
};

const ArchonAppIntegrationContext = createContext<ArchonAppIntegrationContext>({
  isArchonApp: false,
  iframeId: null,
});

export const ArchonAppIntegrationProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const isArchonApp = useIsArchonApp();
  const { data: session, status: sessionStatus } = useSession();
  const router = useRouter();
  const iframeIdParam = router.query.iframeId as string | undefined;
  const [iframeId, setIframeId] = useState<string | null>(null);

  useEffect(() => {
    if (iframeIdParam) {
      setIframeId(iframeIdParam);
    }
  }, [iframeIdParam]);

  useEffect(
    function requestLoginIfArchonApp() {
      if (!isArchonApp || !iframeId || sessionStatus === 'loading') {
        return;
      }
      // We still request login even if sessionStatus === 'authenticated' just in case we're signed in as the wrong user
      window.top?.postMessage(
        {
          message: 'request-login',
          iframeId,
          currentUserId: session?.user.id,
          currentGameSlug: session?.user.gameSlug,
        },
        '*'
      );
    },
    [
      iframeId,
      isArchonApp,
      session?.user.gameSlug,
      session?.user.id,
      sessionStatus,
    ]
  );

  const [signInRequestQueue, setSignInRequestQueue] = useState<
    ArchonAppLoginMessage[]
  >([]);
  const pushSignInRequest = useCallback(
    (request: ArchonAppLoginMessage) =>
      setSignInRequestQueue((prev) => [...prev, request]),
    []
  );
  const nextSignInRequest = useCallback(
    () =>
      setSignInRequestQueue((prev) => {
        if (prev.length === 0) {
          return prev;
        }
        const [, ...rest] = prev;
        return rest;
      }),
    []
  );
  const signInRequest = signInRequestQueue[0] ?? null;

  useEffect(
    function queueSignInRequestOnEvent() {
      function handleMessage(event: MessageEvent<ArchonAppLoginMessage>) {
        if (event.data.message !== 'login') {
          return;
        }
        pushSignInRequest(event.data);
      }

      window.addEventListener('message', handleMessage);

      return () => {
        window.removeEventListener('message', handleMessage);
      };
    },
    [pushSignInRequest]
  );

  useEffect(
    function handleSignInRequest() {
      if (
        !isArchonApp ||
        !iframeId ||
        !signInRequest ||
        sessionStatus === 'loading'
      ) {
        return;
      }
      const { userId, gameSlug, desktopClientToken } = signInRequest;
      if (session?.user.id === userId && session?.user.gameSlug === gameSlug) {
        // Already signed in with the correct user and gameSlug
        nextSignInRequest();
        return;
      }

      signIn('credentials', {
        gameSlug,
        desktopClientToken,
        redirect: false,
      })
        .catch(console.error)
        .finally(nextSignInRequest);
    },
    [
      nextSignInRequest,
      iframeId,
      isArchonApp,
      session?.user.gameSlug,
      session?.user.id,
      sessionStatus,
      signInRequest,
    ]
  );

  useEffect(() => {
    if (iframeId && sessionStatus === 'authenticated' && session) {
      postSessionAuthenticatedMessage(
        iframeId,
        session.user.id,
        session.user.gameSlug
      );
    }
  }, [iframeId, session, sessionStatus]);

  useEffect(() => {
    if (iframeId) {
      postRouteChangeMessage(iframeId, router.asPath);
    }
  }, [iframeId, router.asPath]);

  useEffect(
    function respondToRouteChangeMessage() {
      async function handleMessage(
        event: MessageEvent<ArchonAppRouteChangeMessage>
      ) {
        if (event.data.message !== 'route-change') {
          return;
        }
        const url = new URL(event.data.path, window.location.origin);
        const forceReload = url.searchParams.has('forceReload');
        if (event.data.path === router.asPath && !forceReload) {
          return;
        }
        // In the Archon App, we keep the iframe historyless so that the app can control navigation
        await router.replace(`${url.pathname}${url.search}${url.hash}`);
      }

      window.addEventListener('message', handleMessage);

      return () => {
        window.removeEventListener('message', handleMessage);
      };
    },
    [router]
  );

  return (
    <ArchonAppIntegrationContext.Provider value={{ isArchonApp, iframeId }}>
      {children}
    </ArchonAppIntegrationContext.Provider>
  );
};

function postSessionAuthenticatedMessage(
  iframeId: string,
  userId: number,
  gameSlug: string
) {
  window.top?.postMessage(
    {
      message: 'login-success',
      iframeId,
      userId,
      gameSlug,
    },
    '*'
  );
}

function postRouteChangeMessage(iframeId: string, path: string) {
  window.top?.postMessage(
    {
      message: 'route-change',
      iframeId,
      path,
    },
    '*'
  );
}

export const useArchonAppIntegration = (): ArchonAppIntegrationContext =>
  useContext(ArchonAppIntegrationContext);

export type ArchonAppLoginMessage = {
  message: 'login';
  userId: number;
  gameSlug: string;
  desktopClientToken: string;
};

export type ArchonAppRouteChangeMessage = {
  message: 'route-change';
  path: string;
};

export default useArchonAppIntegration;
